import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectDoesUserHaveRbacPermission } from 'core/userContext';
import {
  getJourneyCurrentConversion,
  selectCampaignMediumsInWorkflow,
  getFlexMetricsDateRange,
  getWorkflow,
  fetchVisitorCountsRequest,
} from 'modules/workflows/ducks';
import { BackendJourneyConversion, Node, Workflow } from 'modules/workflows/types';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { Link } from 'ui/links';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { useWorkflowContext } from 'modules/workflows/context';
import { SEGMENTED_CONTROL_SIZES, SegmentedControl } from 'ui/segmentedControl';
import { MessageMedium } from 'constants/messageMediums';
import { WorkflowView } from 'modules/workflows/constants/workflow';
import { JourneyAnalyticsCard } from './JourneyAnalyticsCard';
import {
  WORKFLOW_ANALYTICS_TEST_ID,
  ANALYTICS_VIEW_INSIGHTS_TEST_ID,
} from './WorkflowAnalytics.constants';
import { AnalyticsContainer, JourneyPanelTitle, StyledTitleText } from './styled';
import { JourneyConversionCard } from './JourneyConversionCard';
import { shouldShowNodeCard } from './WorkflowAnalytics.utils';
import { getInsightsURL } from '../HeaderBar/HeaderBar.utils';
import { WorkflowLiveStatusBadge } from '../HeaderBar/WorkflowTitle';
import { AnalyticsCard } from './AnalyticsCard';
import { BaseCardType } from './NodeAnalyticsCard';
import { InProgressCard } from './InProgressCard';
import { WorkflowType } from 'modules/domains';
import { fetchSendActivity } from 'modules/workflowIndex/actions';

const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const TileStatusContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
`;

const StyledAnalyticsCard = styled(AnalyticsCard)`
  border-radius: 0;
  box-shadow: none !important;
  background-color: transparent;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Divider = styled.div`
  border-top: 1px solid ${colors.borderPrimary};
  margin: 0 -${spacing.base};
  padding: 0 0 ${spacing.threeQuarter} 0;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  margin: ${spacing.half} 0px ${spacing.threeQuarter} 0px;
  display: flex;
  > * {
    flex: 1;
  }
`;

const WorkflowViewSegementControl = () => {
  const {
    setWorkflowView,
    workflowChartConfig: { workflowView },
  } = useWorkflowContext();
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const options = [
    { name: 'Past', value: WorkflowView.Total }, // Past === total throughout the code.
    { name: 'In progress', value: WorkflowView.InProgress },
  ];

  return (
    <StyledSegmentedControl
      size={SEGMENTED_CONTROL_SIZES.LARGE}
      options={options}
      onSelect={(value: any) => {
        if ((value as WorkflowView) === WorkflowView.InProgress) {
          dispatch(
            fetchVisitorCountsRequest(workflow.id, workflow?.workflowType || WorkflowType.DRAFT),
          );
          dispatch(fetchSendActivity([workflow.id]));
        }
        setWorkflowView(value as WorkflowView);
      }}
      value={workflowView === WorkflowView.InProgress ? 'In progress' : 'Total'}
    />
  );
};

type WorkflowAnalyticsCardsProps = {
  workflow: Workflow;
  showViewInsightsLinks?: boolean;
  mediumsInWorkflow: MessageMedium[];
  node?: Node;
  currentConversion?: BackendJourneyConversion;
};

const WorkflowAnalyticsCardsWithJourneyVisitors = ({
  mediumsInWorkflow,
  node,
  currentConversion,
  ...commonProps
}: WorkflowAnalyticsCardsProps) => {
  const {
    workflowChartConfig: { workflowView },
  } = useWorkflowContext();

  return (
    <>
      {<WorkflowViewSegementControl />}
      {workflowView === WorkflowView.InProgress ? (
        <InProgressCard />
      ) : (
        <>
          <JourneyAnalyticsCard
            {...commonProps}
            mediumsInWorkflow={mediumsInWorkflow}
            node={node}
          />
          {currentConversion && (
            <>
              <Divider />
              <JourneyConversionCard
                {...commonProps}
                journeyConversion={currentConversion}
                node={node}
              />
            </>
          )}
        </>
      )}
    </>
  );
};
interface WorkflowAnalyticsProps {
  /** The current workflow */
  workflow: Workflow;
  /** The node currently in focus */
  focusedNodes?: Node[];
}

export const WorkflowAnalytics = ({ workflow, focusedNodes }: WorkflowAnalyticsProps) => {
  const mediumsInWorkflow = useSelector(selectCampaignMediumsInWorkflow);
  const canViewReports = useSelector((state) =>
    // @ts-ignore
    selectDoesUserHaveRbacPermission(state)(PROJECT_PERMISSIONS.ViewReports),
  );
  const canViewInsights = useMemo(
    () => canViewReports && !!mediumsInWorkflow?.length,
    [canViewReports, mediumsInWorkflow],
  );

  const commonProps = {
    workflow,
    showViewInsightsLinks: canViewInsights,
  };

  const currentConversion = useSelector(getJourneyCurrentConversion);

  const nodeCardIsShown = useMemo(() => shouldShowNodeCard(focusedNodes), [focusedNodes]);
  const node = nodeCardIsShown ? focusedNodes?.[0] : undefined;

  const dateRange = useSelector(getFlexMetricsDateRange);
  const url = useMemo(() => {
    return `/${getInsightsURL(mediumsInWorkflow.join(','), `${workflow.id}`, dateRange)}`;
  }, [mediumsInWorkflow, workflow, dateRange]);

  const cardProps = {
    ...commonProps,
    mediumsInWorkflow,
    currentConversion,
    node,
    showViewInsightsLinks: canViewInsights,
  };
  return (
    <AnalyticsContainer>
      <StyledAnalyticsCard
        cardType={BaseCardType.Analytics}
        isCollapsible={true}
        data-test={WORKFLOW_ANALYTICS_TEST_ID}
        title={
          <JourneyPanelTitle>
            <TitleContainer>
              <TileStatusContainer>
                <StyledTitleText>Journey is</StyledTitleText>
                <WorkflowLiveStatusBadge
                  isWorkflowEnabled={!!workflow?.enabled}
                  workflowType={workflow?.workflowType}
                />
              </TileStatusContainer>

              {canViewInsights && (
                <Link
                  data-test={ANALYTICS_VIEW_INSIGHTS_TEST_ID}
                  href={url}
                  hasOpenNewTabIcon
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View insights
                </Link>
              )}
            </TitleContainer>
          </JourneyPanelTitle>
        }
        content={
          <ContentContainer>
            <WorkflowAnalyticsCardsWithJourneyVisitors {...cardProps} />
          </ContentContainer>
        }
      />
    </AnalyticsContainer>
  );
};
